import Grid from '@mui/material/Grid';
import { observer } from 'mobx-react-lite';
import { HintBox, SelectField, Widget } from '../../../components';
import type { HelpItem } from '../../../components/form/HintBox';
import { AccountServicesHelp } from '../../../store/constants/help';
import useFlag from '../../../store/hooks/useFlag';
import type Account from '../store/Account';
import type BunnyStorage from '../store/services/storages/BunnyStorage';
import type PulseCdnStorage from '../store/services/storages/PulseCdnStorage';
import type S3Storage from '../store/services/storages/S3Storage';
import type { AnyStorageType } from '../store/services/storages/types';
import { getStorageProvidersInfo } from '../store/services/types';
import BunnyStorageForm from './BunnyStorageForm';
import PulseCdnStorageForm from './PulseCdnStorageForm';
import S3StorageForm from './S3StorageForm';
import S3StorageRegionForm from './S3StorageRegionForm';

const SERVICES = [
  {
    value: '',
    label: <i>No service selected</i>,
    disabled: true,
    helpText: '',
    helpUrl: '',
  },
  ...getStorageProvidersInfo().map((p) => ({
    ...p,
    helpText: '',
    helpUrl: '',
  })),
];

export default observer(function StorageWidget({
  account,
}: {
  account: Account;
}) {
  const { storages } = account.configurations;
  const provider = storages.selectedProvider as string;
  const [storage = undefined] = storages.selectedServices;

  const usePulseCdn =
    useFlag('usePulseCdn') ||
    storages.findByProvider<PulseCdnStorage>('pulsecdn.net')?.enabled === true;
  const useBunnyCdn =
    useFlag('useBunnyCdn') ||
    storages.filterByProvider('bunnycdn.com').length > 0;

  const help = (
    AccountServicesHelp.services as Record<string, HelpItem | undefined>
  )[provider || 'storage'];

  const services = SERVICES.filter((service) => {
    switch (service.value) {
      case 'bunnycdn.com': {
        return useBunnyCdn;
      }
      case 'pulsecdn.net': {
        return usePulseCdn;
      }
      default: {
        return true;
      }
    }
  });

  return (
    <Widget sx={{ mt: 2 }} title="File Storage">
      <Grid container spacing={6}>
        <Grid item xs={8}>
          <SelectField
            displayEmpty
            label="Service"
            onChange={(e) =>
              storages.setSelectedProvider(e.target.value as any)
            }
            options={services}
            required
            sx={{ mt: 0, width: 256 }}
            value={provider || ''}
          />
          <StorageForm storage={storage} />
        </Grid>
        <Grid item xs={4}>
          <HintBox help={help} />
        </Grid>
      </Grid>
    </Widget>
  );
});

const StorageForm = observer(function StorageForm({
  storage,
}: {
  storage?: AnyStorageType;
}) {
  switch (storage?.provider || '') {
    case '': {
      return null;
    }
    case 'bunnycdn.com': {
      return <BunnyStorageForm storage={storage as BunnyStorage} />;
    }
    case 'cloudflarestorage.com':
    case 'digitaloceanspaces.com': {
      return <S3StorageRegionForm storage={storage as S3Storage} />;
    }
    case 'pulsecdn.net': {
      return <PulseCdnStorageForm storage={storage as PulseCdnStorage} />;
    }
    default: {
      return <S3StorageForm storage={storage as S3Storage} />;
    }
  }
});
